import React, { useState, useEffect } from "react";
import BackButton from "../../BackButton/BackButton";
import ChangeAddress from "../../ChangeAddress/ChangeAddress";
import styles from "./DownloadExchangeLabel.module.css";
import { validateEmail } from "../../utils/common";
import { startEllipsisInterval } from "../../utils/common";
import usePortalSession from "../../../hooks/usePortalSession";
import { useLocation, useNavigate } from "react-router-dom";

const DownloadExchangeLabel = ( { flattenedProducts, exchangeProductData, productsBeingReturned, newProductsSelected, setRenderExchangeConfirmation, setRenderDownloadLabel, orderData, token, apiUrl, font } ) => {
	const [carrierName, setCarrierName] = useState(null);
	const [carrierService, setCarrierService] = useState(null);
	const [customerEmail, setCustomerEmail] = useState(orderData.customer_email);
	const [customerReturnAddress, setCustomerReturnAddress] = useState(orderData.customer_address);
	const [customerReceiveExchangeAddress, setCustomerReceiveExchangeAddress] = useState(orderData.customer_address);
	const [confirmExchangeLabelDownload, setConfirmExchangeLabelDownload] = useState(false);
	const [labelUrl, setLabelUrl] = useState(false);
	const [dynamicText, setDynamicText] = useState("Generating label");
	const [isAnimating, setIsAnimating] = useState(false);
	const [whichAddressWidget, setWhichAddressWidget] = useState(null);
	const carriers = ["USPS", "UPS", "FedEx", "DHL"]; //DHL is not being used currently
	const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const basePath = "/exchanges/";
    const fullPath = location.pathname + location.search;
    const url = fullPath.startsWith(basePath) ? fullPath.slice(basePath.length) : fullPath;
    const feedback = {}; //store feedback choices as well as written feedback here.
    const sessionId = usePortalSession(token, apiUrl);

	flattenedProducts.forEach(product => {
		if (product.selected) { feedback[product.unique_id] = {choices: product.choices, written_feedback: product.written_feedback, unique_id: product.unique_id, product_sku: product.product_sku}; }
	});

	//animated elipses loading effect
    useEffect(() => {
        if (isAnimating) {
            const interval = startEllipsisInterval(setDynamicText, dynamicText);
            return () => clearInterval(interval);
        }
    }, [isAnimating]);

    // Add skus and variant ids
    useEffect(() => {
	  	// productsBeingReturned.forEach(item => { 
	  	// 	const product = exchangeProductData.find(p => p.product_title === item.product_title);
	  	// 	let sku = findSkuFromTitle(product, item.product_variant)
	  	// 	item.product_sku = sku 
	  	// 	const variant = product.shopify_variants.find(v => v.sku === sku);
	  	// 	item.product_variant_id = variant ? variant.id : null;
	  	// });
	  	productsBeingReturned.forEach(item => { 
	  		const product = exchangeProductData.find(p => p.shopify_variants.some(v => v.sku === item.product_sku));
	  		const variant = product.shopify_variants.find(v => v.sku === item.product_sku);
	  		item.product_variant_id = variant ? variant.id : null;
	  	});
	  	newProductsSelected.forEach(item => {
	   		const selectedOptions = Object.entries(item).filter(([key]) => ["Size", "Width", "Gender", "Color"].includes(key)).map(([name, value]) => ({ name, value }));
	    	const product = exchangeProductData.find(p => p.product_title === item.selected_product);
	    	let sku = findSkuFromOptions(product, selectedOptions);
	    	item.product_sku = sku;
	    	const matchingProductSelected = productsBeingReturned.find(return_product => return_product.unique_id === item.unique_id);
	    	item.product_cost = matchingProductSelected.product_cost;
	  	});
	}, [exchangeProductData, productsBeingReturned, newProductsSelected]);

    //data is stored a bit different in productsBeingReturned vs newProductsSelected. This is to extract sku from productsBeingReturned
    const findSkuFromTitle = (match, variantTitle) => {
    	console.log("match: ", match)
	  	if (!match) return null;
	  	const variantMatch = match.shopify_variants.find(v => v.title === variantTitle);
	  	console.log("variant match: ", variantMatch)
	  	return variantMatch ? variantMatch.sku : null;
	};

	//extract sku from newProductsSelected
	const findSkuFromOptions = (match, selectedOptions) => {
	  	if (!match) return null;
	  	return match.shopify_variants.find(v => {
	    	if (v.selectedOptions.length !== selectedOptions.length) return false;
	    		return v.selectedOptions.every(opt => {
	   				return selectedOptions.some(sel => sel.name === opt.name && sel.value === opt.value);
	    });
	  })?.sku || null;
	};

	const setAddress = (new_address) => {
		if (whichAddressWidget === "return") { setCustomerReturnAddress(new_address); }
		else if (whichAddressWidget === "exchange") { setCustomerReceiveExchangeAddress(new_address); }
	}

   //purchase label and provide user with return tracking link and label download url
    const downloadExchangeReturnLabel = async () => {
    	setConfirmExchangeLabelDownload(true);
        setDynamicText("Generating label");
    	setIsAnimating(true);
    	await updateAddress();
		await createShipment();
		setIsAnimating(false);
    };

    const createShipment = async () => {
    	try { 
	    	const response = await fetch(`${apiUrl}/api/create_shipment/${token}/`, {
    			method: "POST", 
    			headers: { 
    				"Content-Type": "application/json",
    				"X-Portal-Session": sessionId
    			},
    			body: JSON.stringify({customer_return_address: customerReturnAddress, customer_receive_exchange_address: customerReceiveExchangeAddress, products_being_returned: productsBeingReturned, new_products_selected: newProductsSelected}),
	    	});
	    	if (response.status === 409) {
                alert("This portal was taken over in another window.");
                navigate("/");
                return;
            }
	        const data = await response.json();
	        const carrier = data.selected_rate.carrier;
	        const service = data.selected_rate.service;
	        setCarrierName(carrier);
	        setCarrierService(service);
	        carriers.forEach(c => {
				if (carrier.includes(c)) { setCarrierName(c); } 
			});
		} catch (error) { console.log(`error creating shipment ${error}`); }
    }

    const downloadShippingLabel = async () => {
    	setDynamicText("Downloading label");
    	setIsAnimating(true);
    	try {
	    	const response = await fetch(`${apiUrl}/api/download_shipping_label/${token}/`, { 
	        	method: "POST", 
	        	headers: { 
	        		"Content-Type": "application/json",
	        		"X-Portal-Session": sessionId
	        	},
	        	body: JSON.stringify({customer_email: customerEmail, feedback: feedback}),
	        });
	        if (response.status === 409) {
                alert("This portal was taken over in another window.");
                navigate("/");
                return;
            }
	        if (!response.ok) { throw new Error("Failed to purchase the label."); }
	        const data = await response.json();
	        const label_url = data.label_url;
	        setIsAnimating(false);
	        window.open(label_url, "_blank");
	    	navigate(`/exchanges_confirm/${url}`);
	    } catch (error) { alert("There was an error downloading your label. Please ensure your address is correct."); }
    }

    const updateAddress = async () => {
    	try {
	    	const response = await fetch(`${apiUrl}/api/update_customer_address/${token}/`, {
	            method: "PUT",
	            headers: { "Content-Type": "application/json" },
	            body: JSON.stringify({ customer_address: customerReturnAddress, ship_to_address: customerReceiveExchangeAddress }),
	        });
	        if (!response.ok) throw new Error("Failed to update customer address.");
	    } catch (error) { console.log(`error updating address ${error}`); }
    }

	return (
		<div className={styles.download_exchange_label_container}>
			<p className={`titles`}>Download Return Label</p>
			<div className={`${styles.download_exchange_label_address_container} fade_in`}>
				{!whichAddressWidget && !confirmExchangeLabelDownload && (
					<>
						<p className={styles.address_title}>Shipping return items from:</p>
						<div className={styles.address_wrapper}>
							<div className={styles.address_text_container}>
								<p className={styles.customer_address}>{customerReturnAddress}</p>
							</div>
							<div className={`${styles.address_button} login_buttons`} onClick={() => { setWhichAddressWidget("return"); }}>Edit</div>
						</div>
						<p className={styles.address_title}>Ship new items to:</p>
						<div className={styles.address_wrapper}>
							<div className={styles.address_text_container}>
								<p className={styles.customer_address}>{customerReceiveExchangeAddress}</p>
							</div>
							<div className={`${styles.address_button} login_buttons`} onClick={() => { setWhichAddressWidget("exchange"); }}>Edit</div>
						</div>
						<div className={styles.customer_info_wrapper}>
							<p className={styles.customer_name}><span style={{ fontWeight: "bold" }}>Customer: &nbsp;</span>{orderData.customer_name}</p>
							<p className={styles.customer_email_title}>Sending confirmation to: &nbsp;&nbsp;</p>
							<input type="text" value={customerEmail} className={styles.customer_email} onChange={(e) => setCustomerEmail(e.target.value)} />
						</div>
						<button className={`login_buttons ${styles.get_label_button} ${font} ${!validateEmail(customerEmail) ? styles.dim : ""}`} onClick={() => { downloadExchangeReturnLabel(); }} disabled={!validateEmail(customerEmail)}>Proceed</button>
					</>
				)}
				{confirmExchangeLabelDownload && (
                    <div className={styles.confirm_exchange_label_container}>
                    	<img src={`${process.env.PUBLIC_URL}/images/remove_popup.png`} alt={"remove_exchange_label_download"} className={`${styles.remove_exchange_label_download}`} onClick={() => { setConfirmExchangeLabelDownload(false); setCarrierName(null); setCarrierService(null); } } />
                    	{/* Only show generic label initially */}
				        {!carrierService && (
				        	<>
				        		<img src={`${process.env.PUBLIC_URL}/images/attach_return_label.png`} alt="attach_return_label" className={`${styles.attach_return_label_image} fade_in`} />
				        		<p className={`${styles.confirming_text}`}>{dynamicText}</p>
				        	</>
				        )}
				        {/* When carrier info is available, fade in logo */}
				        {carrierService && (
				            <>
				                <p className={`${styles.carrier_service_text} fade_in`}>Service: &nbsp; {carrierService}</p>
				                <img src={`${process.env.PUBLIC_URL}/images/${carrierName.toLowerCase()}_logo.png`} alt="carrier_logo" className={`${styles.carrier_logo_image} fade_in`} />
				                {isAnimating && (<p className={`${styles.confirming_text}`}>{dynamicText}</p>)}
				                {!isAnimating && (
				                	<>
				                		<p className={`${styles.label_ready_text} fade_in`}>Download label to complete your exchange.</p>
				                		<div className={`login_buttons ${styles.download_label_button}`} onClick={() => { downloadShippingLabel(); }}>Download</div>
				                	</>
				                )}
				            </>
				        )}          
                    </div>
                )}
				{whichAddressWidget && (<ChangeAddress setNewAddress={(newAddress) => { setAddress(newAddress); setWhichAddressWidget(null); }} />)}
			</div>
			{!whichAddressWidget && (
				<>
					<p className={`titles ${styles.attach_to_parcel}`}>Attach this return label to your parcel.</p>
					<BackButton button_id={styles.exchange_label_download_back_button} image_id={styles.exchange_label_download_back_button_arrow} customBack={() => { setRenderExchangeConfirmation(true); setRenderDownloadLabel(false); }}/>
				</>
			)}
		</div>	
	);
};

export default DownloadExchangeLabel;